import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { Button, ButtonToolbar, Form } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import Select from "react-select";

import genericTranslations from "../../../utils/genericTranslations.js";

const messages = {
  title: {
    id: "modules.SoftwarePackage.components.NotificationForm.title",
    description: "Notification message",
    defaultMessage: "Message",
  },
};

const NotificationForm = ({
  handleChange,
  handleSubmit,
  errors,
  isSubmitting,
  onClickDelete,
  setFieldValue,
  values,
}) => {
  const tagOptions = useMemo(() => {
    if (!values.softwarePackages || !values.softwarePackages.length > 0) {
      return [];
    }

    return values.softwarePackages
      .map(({ id, tag }) => ({
        value: id,
        label: tag,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [values]);

  const selectedTagOptions = useMemo(() => {
    return Array.isArray(values.tag)
      ? values.tag
          .map((tag) => tagOptions.find((option) => option.label === tag))
          .filter(Boolean)
      : [];
  }, [values.tag, tagOptions]);

  const handleSendNotification = async () => {
    await setFieldValue("sendNotification", true);
    handleSubmit();
  };

  const handleChangeTags = (selectedOptions) => {
    const tags = selectedOptions
      ? selectedOptions.map((option) => option.label)
      : [];
    setFieldValue("tag", tags);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="tag" className="mb-3">
        <Form.Label>Tag</Form.Label>
        <Select
          name="tag"
          isClearable
          isMulti
          options={tagOptions}
          value={selectedTagOptions}
          onChange={handleChangeTags}
        />
        <Form.Text
          className="text-muted"
          style={{ fontStyle: "italic", fontSize: 12 }}
        >
          E.g. format #tag1 #tag2
        </Form.Text>
        {errors.tag && (
          <Form.Control.Feedback type="invalid">
            {errors.tag}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      <Form.Group controlId="title" className="mb-3">
        <Form.Label>
          <FormattedMessage {...messages.title} />
        </Form.Label>
        <Form.Control
          type="text"
          autoComplete="off"
          value={values.title}
          onChange={handleChange}
          isInvalid={!values.title}
        />
        {errors.title && (
          <Form.Control.Feedback type="invalid">
            {errors.title}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      <hr />

      <Form.Group controlId="formActions" className="mb-3">
        <ButtonToolbar>
          {values.id && (
            <Button variant="danger" className="me-2" onClick={onClickDelete}>
              <FormattedMessage {...genericTranslations.delete} />
            </Button>
          )}
          <Button
            variant="primary"
            className="me-2"
            onClick={handleSendNotification}
            disabled={isSubmitting || Object.keys(errors).length > 0}
          >
            <FormattedMessage {...genericTranslations.saveAndSend} />
          </Button>
          <Button
            type="submit"
            variant="light"
            disabled={isSubmitting || Object.keys(errors).length > 0}
          >
            <FormattedMessage {...genericTranslations.save} />
          </Button>
        </ButtonToolbar>
      </Form.Group>
    </Form>
  );
};

NotificationForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    title: PropTypes.string,
    tag: PropTypes.string,
  }),
  values: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    tag: PropTypes.string,
    softwarePackages: PropTypes.arrayOf({
      id: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      information: PropTypes.string.isRequired,

      // Last update date
      updatedAt: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default NotificationForm;
